import { useEffect, useState } from "react";
import { Plus, X, Pencil } from "lucide-react";
import Select from "react-select";
import { toast } from "react-toastify";
import chatCategoryService from "../services/chatCategoryService";
import agentService from "../services/agentService";
import assistantService from "../services/assistantService";
import { useDashboardUI } from "../context/GlobalContext";

const CATEGORY_NAME_MAX_LENGTH = 100;
const CATEGORY_DESCRIPTION_MAX_LENGTH = 500;

const FIELD_LABELS = { name: "Name", description: "Description" };

// At least one letter or digit — rejects a value that's just punctuation,
// whitespace, or repeated symbols (e.g. "!!!", "_____"). Mirrors the same
// check the backend enforces in app.crud.chat_categories.
const hasMeaningfulContent = (value) => /[\p{L}\p{N}]/u.test(value);

// A 422 from FastAPI/Pydantic sends `detail` as a list of per-field errors,
// not a string — client-side maxLength + the pre-submit check above mean
// this should never actually fire, but translate it into the same plain,
// human copy as every other inline error here just in case (never the raw
// backend message).
function formatApiError(err, fallback = "Something went wrong — please try again.") {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]) {
        const item = detail[0];
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        const label = FIELD_LABELS[field] || "This field";
        if (item?.type === "string_too_long" && item?.ctx?.max_length) {
            return `${label} must be ${item.ctx.max_length} characters or fewer.`;
        }
        return `${label} is invalid.`;
    }
    return fallback;
}

function CategoryModal({ initial, onClose, onSave }) {
    const isEdit = Boolean(initial);
    const [name, setName] = useState(initial?.name || "");
    const [description, setDescription] = useState(initial?.description || "");
    const [isDefault, setIsDefault] = useState(initial?.is_default ?? false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedName = name.trim();
        const trimmedDescription = description.trim();
        if (!trimmedName || !trimmedDescription) {
            setError("Name and description are both required.");
            return;
        }
        if (!hasMeaningfulContent(trimmedName)) {
            setError("Name must contain letters or numbers, not just symbols or whitespace.");
            return;
        }
        if (!hasMeaningfulContent(trimmedDescription)) {
            setError("Description must contain letters or numbers, not just symbols or whitespace.");
            return;
        }
        if (trimmedName.length > CATEGORY_NAME_MAX_LENGTH) {
            setError(`Name must be ${CATEGORY_NAME_MAX_LENGTH} characters or fewer.`);
            return;
        }
        if (trimmedDescription.length > CATEGORY_DESCRIPTION_MAX_LENGTH) {
            setError(`Description must be ${CATEGORY_DESCRIPTION_MAX_LENGTH} characters or fewer.`);
            return;
        }
        setSaving(true);
        setError("");
        try {
            if (isEdit) {
                await chatCategoryService.updateCategory(initial.id, {
                    name: trimmedName,
                    description: trimmedDescription,
                    is_default: isDefault,
                });
            } else {
                await chatCategoryService.createCategory({
                    name: trimmedName,
                    description: trimmedDescription,
                    is_default: isDefault,
                });
            }
            onSave();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const nameTooLong = name.trim().length > CATEGORY_NAME_MAX_LENGTH;
    const descriptionTooLong = description.trim().length > CATEGORY_DESCRIPTION_MAX_LENGTH;
    const nameLooksMeaningless = Boolean(name.trim()) && !hasMeaningfulContent(name.trim());
    const descriptionLooksMeaningless =
        Boolean(description.trim()) && !hasMeaningfulContent(description.trim());

    const nameFieldError = nameTooLong
        ? `Name must be ${CATEGORY_NAME_MAX_LENGTH} characters or fewer.`
        : nameLooksMeaningless
        ? "Name must contain letters or numbers, not just symbols or whitespace."
        : "";
    const descriptionFieldError = descriptionTooLong
        ? `Description must be ${CATEGORY_DESCRIPTION_MAX_LENGTH} characters or fewer.`
        : descriptionLooksMeaningless
        ? "Description must contain letters or numbers, not just symbols or whitespace."
        : "";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-semibold text-slate-900">
                        {isEdit ? "Edit Category" : "Add Category"}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-medium text-slate-500">Name</label>
                            <span className="text-[10px] text-slate-400">
                                {name.length}/{CATEGORY_NAME_MAX_LENGTH}
                            </span>
                        </div>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Vendor Escalation"
                            maxLength={CATEGORY_NAME_MAX_LENGTH}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            autoFocus
                        />
                        {nameFieldError && (
                            <p className="text-xs text-red-600 mt-1">{nameFieldError}</p>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-medium text-slate-500">Description</label>
                            <span className="text-[10px] text-slate-400">
                                {description.length}/{CATEGORY_DESCRIPTION_MAX_LENGTH}
                            </span>
                        </div>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What kind of interaction belongs in this category? This is shown to the classifier, so be specific."
                            rows={4}
                            maxLength={CATEGORY_DESCRIPTION_MAX_LENGTH}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                        />
                        {descriptionFieldError && (
                            <p className="text-xs text-red-600 mt-1">{descriptionFieldError}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1.5">Type</label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                                <input
                                    type="radio"
                                    name="category-type"
                                    checked={!isDefault}
                                    onChange={() => setIsDefault(false)}
                                />
                                Custom
                            </label>
                            <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                                <input
                                    type="radio"
                                    name="category-type"
                                    checked={isDefault}
                                    onChange={() => setIsDefault(true)}
                                />
                                Default
                            </label>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                            Default categories classify every chat that has no assistant/agent
                            override. Custom categories are excluded from that global set — they
                            only apply once explicitly added to a specific assistant's or agent's
                            overrides.
                        </p>
                    </div>
                    {error && <p className="text-xs text-red-600">{error}</p>}
                    <div className="flex justify-end gap-2 mt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-sm font-medium text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={
                                saving ||
                                nameTooLong ||
                                descriptionTooLong ||
                                nameLooksMeaningless ||
                                descriptionLooksMeaningless
                            }
                            className="text-sm font-medium text-white bg-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Add category"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function TabButton({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            className={`flex-1 sm:flex-none whitespace-nowrap text-sm font-semibold px-3 py-1.5 rounded-lg transition ${
                active ? "bg-[#14122B] text-white" : "text-gray-500 hover:bg-gray-50"
            }`}
        >
            {children}
        </button>
    );
}

// Phones/tablets: the intro text is clamped to 2 lines with a Read more / Read less
// toggle; from lg up the full text is shown as before and the toggle is hidden.
function ReadMoreText({ children, className = "" }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className={`min-w-0 ${className}`}>
            <p className={`text-sm text-slate-500 ${expanded ? "" : "line-clamp-2 lg:line-clamp-none"}`}>
                {children}
            </p>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-1 text-xs font-semibold text-[#3F4BB8] hover:underline lg:hidden"
            >
                {expanded ? "Read less" : "Read more"}
            </button>
        </div>
    );
}

function CategoriesSection() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalState, setModalState] = useState(null); // null | { editing: category|null }
    const [togglingId, setTogglingId] = useState(null);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const data = await chatCategoryService.getAllCategories();
            setCategories(data?.categories || []);
        } catch (error) {
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleToggleActive = async (category) => {
        setTogglingId(category.id);
        try {
            await chatCategoryService.updateCategory(category.id, { is_active: !category.is_active });
            await fetchCategories();
        } finally {
            setTogglingId(null);
        }
    };

    const handleSaved = () => {
        setModalState(null);
        fetchCategories();
    };

    return (
        <div className="flex flex-col gap-4 h-full min-h-0">
            <div className="flex flex-row items-start gap-3 sm:items-center sm:justify-between shrink-0">
                <ReadMoreText className="min-w-0 flex-1 max-w-2xl">
                    The interaction taxonomy used to classify every query/response pair. New
                    categories can be added here — none can be removed, only marked inactive so
                    they stop being offered to the classifier going forward.
                </ReadMoreText>
                <button
                    onClick={() => setModalState({ editing: null })}
                    className="flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap text-white bg-[#3F4BB8] px-4 sm:px-6 h-11 rounded-xl hover:bg-[#363FA0] transition shrink-0 self-start sm:self-auto"
                >
                    <Plus className="w-4 h-4" />
                    Add<span className="hidden sm:inline"> Category</span>
                </button>
            </div>

            <div className="flex flex-col flex-1 min-h-0 rounded-2xl bg-white overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
                {loading ? (
                    <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
                        Loading categories…
                    </div>
                ) : categories.length === 0 ? (
                    <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
                        No categories yet
                    </div>
                ) : (
                    <div className="table-scroll flex-1 min-h-0 overflow-auto">
                    <table className="w-full min-w-[800px] table-fixed border-collapse text-sm text-left">
                        <colgroup>
                            <col style={{ width: "20%" }} />
                            <col style={{ width: "40%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "16%" }} />
                            <col style={{ width: "12%" }} />
                        </colgroup>
                        <thead>
                            <tr className="border-b border-gray-100">
                                {["Name", "Description", "Type", "Status", "Actions"].map((label) => (
                                    <th
                                        key={label}
                                        className={`sticky top-0 z-10 bg-[#F5F2FF] py-3.5 align-middle text-xs font-semibold uppercase tracking-wide text-gray-500 ${
                                            label === "Actions" ? "px-3 text-center" : "pl-6 pr-3 text-left"
                                        }`}
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((category) => (
                                <tr key={category.id} className="border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50">
                                    <td
                                        className="pl-6 pr-3 py-4 text-left text-sm font-semibold text-[#14122B] align-middle truncate"
                                        title={category.name}
                                    >
                                        {category.name}
                                    </td>
                                    <td
                                        className="pl-6 pr-3 py-4 text-left text-gray-600 align-middle truncate"
                                        title={category.description}
                                    >
                                        {category.description}
                                    </td>
                                    <td className="pl-6 pr-3 py-4 text-left align-middle whitespace-nowrap">
                                        <span
                                            className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                                category.is_default
                                                    ? "bg-[#F1F2F7] text-[#4B5266]"
                                                    : "bg-[#E8EAFB] text-[#3F4BB8]"
                                            }`}
                                        >
                                            {category.is_default ? "Default" : "Custom"}
                                        </span>
                                    </td>
                                    <td className="pl-6 pr-3 py-4 text-left align-middle whitespace-nowrap">
                                        <button
                                            onClick={() => handleToggleActive(category)}
                                            disabled={togglingId === category.id}
                                            className={`flex items-center gap-2 text-xs font-medium ${
                                                category.is_active ? "text-emerald-600" : "text-slate-400"
                                            }`}
                                            title={category.is_active ? "Click to deactivate" : "Click to reactivate"}
                                        >
                                            <span
                                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                                    category.is_active ? "bg-emerald-500" : "bg-slate-200"
                                                } ${togglingId === category.id ? "opacity-50" : ""}`}
                                            >
                                                <span
                                                    className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                                                    style={{ transform: category.is_active ? "translateX(18px)" : "translateX(2px)" }}
                                                />
                                            </span>
                                            {category.is_active ? "Active" : "Inactive"}
                                        </button>
                                    </td>
                                    <td className="px-3 py-4 text-center align-middle whitespace-nowrap">
                                        <button
                                            onClick={() => setModalState({ editing: category })}
                                            className="text-slate-400 hover:text-indigo-600"
                                            title="Edit"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>

            {modalState && (
                <CategoryModal
                    initial={modalState.editing}
                    onClose={() => setModalState(null)}
                    onSave={handleSaved}
                />
            )}
        </div>
    );
}

function OverrideRow({ name, subtitle, kindLabel, currentIds, categoryOptions, onSave }) {
    const [selectedIds, setSelectedIds] = useState(currentIds || []);
    const [saving, setSaving] = useState(false);

    const dirty =
        JSON.stringify([...selectedIds].sort()) !== JSON.stringify([...(currentIds || [])].sort());

    const options = categoryOptions.map((c) => ({
        value: c.id,
        label: c.is_active ? c.name : `${c.name} (inactive)`,
    }));

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(selectedIds);
            toast.success(`Saved category overrides for ${name}`);
        } catch (err) {
            toast.error("Failed to save — please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <tr className="border-b border-gray-100 last:border-0 align-middle transition-colors hover:bg-gray-50">
            <td className="pl-6 pr-3 py-4 text-left">
                <div className="truncate text-sm font-semibold text-[#14122B]" title={name}>{name}</div>
                {subtitle && <div className="truncate text-xs text-[#9AA1B2]" title={subtitle}>{subtitle}</div>}
            </td>
            <td className="pl-6 pr-3 py-4 text-left whitespace-nowrap">
                <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        kindLabel === "Agent" ? "bg-[#F3EAFE] text-[#7C3AED]" : "bg-[#E8EAFB] text-[#3F4BB8]"
                    }`}
                >
                    {kindLabel}
                </span>
            </td>
            <td className="pl-6 pr-3 py-4 text-left">
                <Select
                    isMulti
                    options={options}
                    value={options.filter((o) => selectedIds.includes(o.value))}
                    onChange={(vals) => setSelectedIds(vals.map((v) => v.value))}
                    placeholder="Default categories apply — select to override"
                    classNamePrefix="react-select"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
            </td>
            <td className="px-3 py-4 text-center whitespace-nowrap">
                <button
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    className="text-sm font-semibold text-white bg-[#3F4BB8] px-4 py-2 rounded-xl hover:bg-[#363FA0] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {saving ? "Saving…" : "Save"}
                </button>
            </td>
        </tr>
    );
}

function OverridesTable({ description, items, loading, emptyLabel }) {
    return (
        <div className="flex flex-col gap-4 h-full min-h-0">
            <ReadMoreText className="max-w-2xl shrink-0">{description}</ReadMoreText>
            <div className="flex flex-col flex-1 min-h-0 rounded-2xl bg-white overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
                {loading ? (
                    <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
                        {emptyLabel}
                    </div>
                ) : (
                    <div className="table-scroll flex-1 min-h-0 overflow-auto">
                    <table className="w-full min-w-[860px] table-fixed border-collapse text-sm text-left">
                        <colgroup>
                            <col style={{ width: "22%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "52%" }} />
                            <col style={{ width: "14%" }} />
                        </colgroup>
                        <thead>
                            <tr className="border-b border-gray-100">
                                {["Name", "Type", "Category overrides", "Action"].map((label) => (
                                    <th
                                        key={label}
                                        className={`sticky top-0 z-10 bg-[#F5F2FF] py-3.5 align-middle text-xs font-semibold uppercase tracking-wide text-gray-500 ${
                                            label === "Action" ? "px-3 text-center" : "pl-6 pr-3 text-left"
                                        }`}
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>{items}</tbody>
                    </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function OverridesSection({ categoryOptions }) {
    const { selectedFacilityFilter } = useDashboardUI();
    const [assistants, setAssistants] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const facilityNumber = selectedFacilityFilter?.facility_number;
            const query = facilityNumber ? `&facility_number=${facilityNumber}` : "";
            const [assistantData, agentData] = await Promise.all([
                assistantService.getAssistantList(query),
                agentService.getAllAgents(),
            ]);
            setAssistants(assistantData?.results || []);
            setAgents(agentData?.agents || []);
        } catch (error) {
            setAssistants([]);
            setAgents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, [selectedFacilityFilter]);

    const items = [
        ...assistants.map((assistant) => (
            <OverrideRow
                key={`assistant-${assistant.id}`}
                name={assistant.name}
                kindLabel="Assistant"
                currentIds={assistant.category_ids}
                categoryOptions={categoryOptions}
                onSave={async (ids) => {
                    await assistantService.updateAssistant(assistant.id, { category_ids: ids });
                    await fetchAll();
                }}
            />
        )),
        ...agents.map((agent) => (
            <OverrideRow
                key={`agent-${agent.id}`}
                name={agent.name}
                subtitle={agent.slug}
                kindLabel="Agent"
                currentIds={agent.category_ids}
                categoryOptions={categoryOptions}
                onSave={async (ids) => {
                    await agentService.updateAgent(agent.id, { category_ids: ids });
                    await fetchAll();
                }}
            />
        )),
    ];

    return (
        <OverridesTable
            description="Pick at least 2 categories to classify a specific assistant's or agent's interactions against exactly that set instead of the global defaults. Fewer than 2 falls back to the defaults automatically. Assistants are scoped to the facility currently selected in the top bar; agents are global."
            loading={loading}
            emptyLabel="No assistants or agents found"
            items={items}
        />
    );
}

export default function ChatCategories() {
    const [tab, setTab] = useState("categories");
    const [categoryOptions, setCategoryOptions] = useState([]);

    useEffect(() => {
        chatCategoryService
            .getAllCategories()
            .then((data) => setCategoryOptions(data?.categories || []))
            .catch(() => setCategoryOptions([]));
    }, [tab]);

    return (
        <div className="w-full flex flex-col gap-4 pt-4 h-full lg:h-[calc(100vh-100px)]">
            <div className="flex w-full shrink-0 gap-1 bg-white shadow-sm rounded-2xl p-1.5 sm:inline-flex sm:w-fit">
                <TabButton active={tab === "categories"} onClick={() => setTab("categories")}>
                    Categories
                </TabButton>
                <TabButton active={tab === "overrides"} onClick={() => setTab("overrides")}>
                    Assistants & Agents
                </TabButton>
            </div>

            <div className="flex-1 min-h-0">
                {tab === "categories" && <CategoriesSection />}
                {tab === "overrides" && <OverridesSection categoryOptions={categoryOptions} />}
            </div>
        </div>
    );
}
